import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { baseUrl } from '../../utils/axios'
import { toast } from 'react-toastify'
import { handleErrors, formatParams, getParams } from 'utils/helpers'
import { AxiosResponse } from 'axios'
import { IPaginatedResponse } from 'types/api/general.interface'
import { ICompanyAdminsSlice } from 'types/companyAdmins.interface'
import {
  ICompanyAdminCreateRequest,
  ICompanyAdminSingleResponse,
} from 'types/api/companyAdminsApi.interface'
import { ICompanyAdminsModalState } from 'pages/CompanyAdmins/CompanyAdminModal'

const initialState: ICompanyAdminsSlice = {
  isLoading: false,
  admins: [],
  totalCount: 0,
  totalPages: 0,
}

export const getCompanyAdmins = createAsyncThunk(
  'companyAdmins/getAllCompanyAdmins',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<ICompanyAdminSingleResponse>> =
        await baseUrl.get('company/administrator', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const addCompanyAdmin = createAsyncThunk(
  'companyAdmins/addCompanyAdmin',
  async (state: ICompanyAdminsModalState, thunkAPI) => {
    try {
      const req: ICompanyAdminCreateRequest = {
        firstName: state.firstName,
        lastName: state.lastName,
        username: state.username,
        email: state.email,
        phoneNumber: state.phoneNumber,
        companyId: state.company.value,
      }
      const resp: AxiosResponse<ICompanyAdminSingleResponse> = await baseUrl.post(
        'company/administrator',
        req,
      )
      await thunkAPI.dispatch(getCompanyAdmins(getParams({ location })))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const editCompanyAdmin = createAsyncThunk(
  'companyAdmins/editCompanyAdmin',
  async (state: ICompanyAdminsModalState, thunkAPI) => {
    try {
      const req: Omit<ICompanyAdminCreateRequest, 'username'> = {
        firstName: state.firstName,
        lastName: state.lastName,
        email: state.email,
        phoneNumber: state.phoneNumber,
        companyId: state.company.value,
      }
      const resp: AxiosResponse<ICompanyAdminSingleResponse> = await baseUrl.patch(
        'company/administrator/' + state.id,
        req,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const deleteCompanyAdmin = createAsyncThunk(
  'companyAdmins/deleteCompanyAdmin',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.delete('company/administrator/' + id)
      await thunkAPI.dispatch(getCompanyAdmins(getParams({ location })))

      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const companyAdminsSlice = createSlice({
  name: 'companyAdmins',
  initialState,
  reducers: {
    clearCompanyAdmins: (state) => {
      state.admins = []
    },
  },

  extraReducers: {
    [getCompanyAdmins.pending.type]: (state) => {
      state.isLoading = true
    },
    [getCompanyAdmins.fulfilled.type]: (
      state: ICompanyAdminsSlice,
      { payload }: { payload: IPaginatedResponse<ICompanyAdminSingleResponse> },
    ) => {
      state.admins = payload.data
      state.totalCount = payload.totalCount
      state.totalPages = payload.totalPages
      state.isLoading = false
    },
    [getCompanyAdmins.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },

    [deleteCompanyAdmin.pending.type]: (state) => {
      state.isLoading = true
    },
    [deleteCompanyAdmin.fulfilled.type]: (state: ICompanyAdminsSlice) => {
      toast.success('Company administrator deleted successfully')
      state.isLoading = false
    },
    [deleteCompanyAdmin.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [addCompanyAdmin.pending.type]: (state) => {
      state.isLoading = true
    },
    [addCompanyAdmin.fulfilled.type]: (state: ICompanyAdminsSlice) => {
      toast.success('Company administrator created successfully')
      state.isLoading = false
    },
    [addCompanyAdmin.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [editCompanyAdmin.pending.type]: (state) => {
      state.isLoading = true
    },
    [editCompanyAdmin.fulfilled.type]: (
      state: ICompanyAdminsSlice,
      { payload }: { payload: ICompanyAdminSingleResponse },
    ) => {
      state.admins = state.admins.map((a) => (a.id === payload.id ? payload : a))
      toast.success('Company administrator updated successfully')
      state.isLoading = false
    },
    [editCompanyAdmin.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
  },
})

export const { clearCompanyAdmins } = companyAdminsSlice.actions
export default companyAdminsSlice.reducer
